<template>
  <div :class="componentClasses">
    <slot />
    <slot
      v-if="showSidebar"
      name="other"
    />

    <!-- Speaker notes toggle -->
    <div v-if="!optimizer" class="toggle">
      <p>Show speaker notes</p>
      <Toggle v-model="showSidebar" />
    </div>

    <!-- Set custom fees — rendered last and visually separated. Independent of
         the other toggles: you don't have to set fees to include the page in
         the presentation. -->
    <template v-if="showFeesToggle">
      <hr class="tw-border-t tw-border-gray-200 tw-my-2">
      <div class="toggle">
        <p>Set custom fees</p>
        <Toggle v-model="showFees" />
      </div>
      <!-- Collapsed with height/opacity rather than `v-show` (display:none):
           a display:none subtree is dropped from the layout tree, so each
           open forces a full synchronous relayout of the heavy Oruga fee
           tree (~slow). Clipping with overflow keeps the subtree laid out
           once (at mount) and retained, so toggling is a cheap visibility
           flip. Stays mounted, so in-progress tier edits aren't lost. -->
      <div
        class="fees-panel"
        :class="{ 'fees-panel--collapsed': !showFees }"
        :aria-hidden="!showFees"
      >
        <slot
          v-if="feesMounted"
          name="fees"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { Toggle } from '@stakeui';

const props = defineProps({
  optimizer: {
    type: Boolean,
    default: false,
  },
  showFeesToggle: {
    type: Boolean,
    default: false,
  },
});

const showSidebar = ref(false);
const showFees = ref(false);

// The custom-fees panel is a heavy Oruga tree (3 tier lists + many number
// inputs). Keep it off the area's initial render: mount it lazily once the
// area is idle, then keep it mounted (so opening is instant and in-progress
// tier edits survive a close). Open also forces a mount in case the user
// clicks before the idle callback fires.
const feesMounted = ref(false);
const mountFees = () => {
  feesMounted.value = true;
};
watch(showFees, value => {
  if (value) mountFees();
});
onMounted(() => {
  if (!props.showFeesToggle) return;
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(mountFees);
  } else {
    setTimeout(mountFees, 200);
  }
});

const componentClasses = computed(() => [
  'tw-flex tw-flex-col tw-bg-white tw-text-[#1F2229] tw-text-sm tw-gap-3 tw-p-5 tw-rounded-lg',
  props.optimizer ? '' : 'area-sidebar',
  // Only widen when the fee panel is present (its tier inputs need the room).
  // Without it the sidebar keeps its original width.
  props.showFeesToggle ? 'area-sidebar--wide' : '',
]);
</script>

<style scoped>
.area-sidebar {
  position: fixed;
  box-shadow: 0 0.5em 1em rgba(10, 10, 10, 0.2);
  right: 1rem;
  bottom: 1rem;
  z-index: 20;
  width: 18rem;
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
}

/* Wider variant for the custom-fees panel (tier inputs need the room). */
.area-sidebar--wide {
  width: 22rem;
}

.toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Collapse without display:none so the heavy fee tree stays in the layout
   tree (laid out once) and re-opening is a cheap visibility flip. */
.fees-panel--collapsed {
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}
</style>
